import { useState, useEffect } from "react";
import { bindActionCreators, Dispatch } from "redux";
import { connect, RootStateOrAny } from "react-redux";

import { Alert, Button, Divider, Form, Modal, Popconfirm, Select, Table, Typography, message } from "antd";
import _ from "lodash";
import moment from "moment";
import Dragger from "antd/lib/upload/Dragger";
import { DeleteFilled, InboxOutlined } from "@ant-design/icons";
import { UploadFile } from "antd/lib/upload/interface";

import { downloadFile } from 'store/authorization/files/actions';
import { DocumentFiles, uploadProviderResources, searchProviderByName, searchSpecialtyName, deleteProviderResources, updateProviderResources } from "store/app/ipa/actions";
import { getProviderByFileId, getSpecByFileId } from 'store/eligibility/documents/actions';

//eligibility info page related
import { SearchCriteria, search } from "store/eligibility/actions";
import { getCategoryDocsForMemDocPage } from "store/eligibility/documents/actions";
import { updateReadInfo } from "store/eligibility/pac/actions";
import { getAllDesc as getAllIpasDesc } from 'store/app/ipa/actions';

import { ErrorResponse } from "utils/types";
import { formatDate, getDisplayCompanyId, handleDownload } from "utils/helpers";
import notifier from "utils/notifier";
import { DocumentCategory, UserRole } from "utils/enums";

import styles from './MemoPage.module.scss';

const { Title } = Typography;

interface MemoPageProps {
  updateReadInfo(passedUserId: string, passedPacUid: string, passedPacType: string): any;
  getMemoDocuments(materialType: string, measureCode: string, companyId: string): any;
  uploadProviderResources(params: any): any;
  getAllIpasDesc(): any;
  deleteProviderResources(params: any): any;
  searchSpecialtyName(UserId: string, PageSize: string, ProviderName: string, CompanyIds: any): any;
  searchProviderByName(UserId: string, PageSize: string, SpecialtyName: string, CompanyIds: string[]): any;
  getCategoryDocsForMemDocPage(userId: string, categoryDescArray: string[]): any;
  getProviderByFileId(fileId: number): any;
  getSpecByFileId(fileId: number): any;
  updateProviderResources(params: any): any;
  downloadFile(companyId: string, filePath: string): any;
  isUpdatingDocument: boolean;
  isUploadingProvResources: boolean;
  isFetchingForMemDoc: boolean;

  //eligibility info page related
  search(searchCriteria: SearchCriteria): any;
}

const MemoPage = (props: MemoPageProps) => {
  const [form] = Form.useForm();
  const [documentList, setDocumentList] = useState<any>([]);
  const [fileAttachments, setFileAttachments] = useState<any[]>([]);
  const [isAddNewDocumentsVisible, setIsAddNewDocumentsVisible] = useState(false);
  const [showEditDocumentModal, setShowEditDocumentModal] = useState(false);
  const [editModalData, setEditModalData] = useState<any>({});
  const [companyIds, setCompanyIds] = useState<any>([]);
  const [specialties, setSpecialties] = useState<any>([]);
  const [providerNames, setProviderNames] = useState<any>([]);
  const [memoDataSource, setMemoDataSource] = useState<Object[]>([]);
  const [defaultSpecOptions, setDefaultSpecOptions] = useState<any>([]);
  const [defaultProviderNamesOptions, setDefaultProviderNamesOptions] = useState<any>([]);

  useEffect(() => {
    getMemoDocuments();
    getAllIpasDesc();
  }, []);

  const memoColumns: any = [
    {
      title: "IPA",
      key: "RmiCompanyId",
      dataIndex: "RmiCompanyId",
      render: (text: string) => getDisplayCompanyId(text),
      sorter: (a: any, b: any) => a.RmiCompanyId.localeCompare(b.RmiCompanyId),
    },
    {
      title: "File Name",
      key: "RmiFileName",
      dataIndex: "RmiFileName",
      render: (_: string, record: any) => (
        <a onClick={() => downloadMemberDocument(record, DocumentCategory.MEMOS)}>
          {record.RmiFileName}
        </a>
      ),
      sorter: (a: any, b: any) => a.RmiFileName.localeCompare(b.RmiFileName),
    },
    {
      title: "Upload Date",
      key: "RmiUploadDate",
      dataIndex: "RmiUploadDate",
      render: (_: string, record: any) => formatDate(record.RmiUploadDate, "MM/DD/YYYY [at] h:mmA"),
      sorter: (a: any, b: any) => a.RmiUploadDate.localeCompare(b.RmiUploadDate),
    },
    {
      title: "Read By",
      key: "RmiReadBy",
      dataIndex: "RmiReadBy",
      sorter: (a: any, b: any) => {
        a = a.RmiReadBy || '';
        b = b.RmiReadBy || '';
        return a.localeCompare(b);
      },
    },
    {
      title: "Read Date",
      key: "RmiReadDate",
      dataIndex: "RmiReadDate",
      render: (_: string, record: any) => formatDate(record.RmiReadDate, "MM/DD/YYYY [at] h:mmA"),
      sorter: (a: any, b: any) =>  {
        a = a.RmiReadDate || '';
        b = b.RmiReadDate || '';
        return a.localeCompare(b);
      },
    },
  ];

  if (sessionStorage.user_role === UserRole.ADMIN) {
    memoColumns.push({
      title: "Admin",
      key: "admin",
      dataIndex: "admin",
      render: (_: string, record: any) =>
        <>
          <i className="fa fa-solid fa-pen mr-2" onClick={() => onEditDocument(record)}></i>
          <Popconfirm
            placement="top"
            title={<><div>Are you sure to delete this file?</div><b>{record.RmiFileName}</b></>}
            onConfirm={() => deleteProviderResources(record)}
            okText="Yes"
            cancelText="No"
          >
            <i className="fa fa-solid fa-trash"></i>
          </Popconfirm>
        </>
    });
  }

  const closeEditDocuments = () => {
    setEditModalData({});
    setShowEditDocumentModal(false);
  };

  const closeAddNewDocuments = () => {
    setDocumentList([]);
    setIsAddNewDocumentsVisible(false);
    setFileAttachments([]);
  };

  const beforeUpload = (file: UploadFile) => {
    const ext = file.name.substr(file.name.lastIndexOf('.') + 1);
    const allowFiles = ['tif', 'tiff', 'jpg', 'jpeg', 'pdf', 'doc', 'docx','TIF','TIFF','JPG','JPEG','PDF','DOC','DOCX'].includes(ext);
    if (!allowFiles) {
      message.error('You can only upload tif, tiff, jpg, jpeg, pdf, doc, docx, TIF, TIFF, JPG, JPEG, PDF, DOC, or DOCX file!');
      return false;
    } else {
      //file attachment used to list out the files under the dragger and cannot have duplicate files attached
      if (fileAttachments.filter(attachment => attachment.name === file.name).length === 0) {
        setFileAttachments((previous: any) => ([...previous, file]));
        setDocumentList((previous: any) => ([...previous, {
          RmiCompanyId: '',
          RmiCreatedBy: '',
          RmiCreatedDate: '',
          RmiFileName: '',
          RmiFileDesc: '',
          RmiLanguage: '',
          RmiLocationPath: '',
          RmiMaterialType: '',
          RmiModifiedBy: '',
          RmiModifiedDate: '',
          File: file,
          CompanyIds: companyIds.length === 1 ? [companyIds[0].value] : '',
        }]));
      } else {
        notifier.error('Please attach a file with a different name.');
      }
      return false;
    };
  };

  //Update Read Info API after you click on download member document link
  const updateReadInfo = (document: any, pacType: string) => {
    return props.updateReadInfo(sessionStorage.user_id, document.RmiUid, pacType)
      .then(() => {
        getMemoDocuments();
      })
      .catch((err: ErrorResponse) =>
        notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.')
      );
  }

  const getMemoDocuments = () => {
    const documentCategories = [DocumentCategory.MEMOS];
    return props.getCategoryDocsForMemDocPage(sessionStorage.user_id, documentCategories)
      .then((response: Object[]) => {
        setMemoDataSource(_.orderBy(response, 'RmiUploadDate', 'desc'));
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.');
      });
  };

  const downloadMemberDocument = (documentFile: any, pacType: string) => {
    return props.downloadFile(documentFile.RmiCompanyId, documentFile.RmiFilePath)
      .then((response: any) => {
        handleDownload(response, documentFile.RmiFileName);
        updateReadInfo(documentFile, pacType);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.')
      });
  }

  const onEditDocument = (record: any) => {
    setShowEditDocumentModal(true);
    setEditModalData({ RmiFileName: record.RmiFileName, RmiCompanyId: record.RmiCompanyId, RmiUid: record.RmiUid });
    props.getProviderByFileId(record.RmiUid)
      .then((response: any) => {
        const defaultProviderNames: any = [];
        const defaultOptions: any = [];
        for (const value of response) {
          defaultProviderNames.push(`${value.RmiRevFullName} - [${value.RmiProvId}]`);
          defaultOptions.push({
            value: `${value.RmiRevFullName} - [${value.RmiProvId}]`
          });
        }
        setEditModalData((previous: any) => ({...previous, ProviderIds: defaultProviderNames}));
        setDefaultProviderNamesOptions(defaultOptions);
    })
    .catch((err: ErrorResponse) => {
      notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
    });

    props.getSpecByFileId(record.RmiUid)
      .then((response: any) => {
        const specCodes: any = [];
        const options: any = [];
        for (const value of response) {
          specCodes.push(`${value?.RmiDescr} - [${value?.RmiCode}]`);
          options.push({
            value: `${value?.RmiDescr} - [${value?.RmiCode}]`
          });
        }

        setEditModalData((previous: any) => ({ ...previous, SpecCodes: specCodes }));
        setDefaultSpecOptions(options);
    })
    .catch((err: ErrorResponse) => {
      notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
    });
  }

  const submitNewDocument = async () => {
    let isValid = true;

    for (const value of documentList) {
      if (!value.CompanyIds) {
        notifier.error(`Please fill Company ID(s) for ${value.File.name}`);
        isValid = false;
      }
    }

    if (isValid) {
      Promise.all(documentList.map(async (currentValue: any) => {
        const formData = new FormData();
        formData.append("RmiCreatedBy", sessionStorage.getItem('user_id')!);
        formData.append("RmiCreatedDate", moment().toString());
        formData.append("RmiFileDesc", currentValue.File.name);
        formData.append("RmiFileName", currentValue.File.name);
        formData.append("RmiLanguage", "English");
        formData.append("RmiLocationPath", "Forms\\Memo\\");
        formData.append("RmiMaterialType", "MEMO");
        formData.append("RmiModifiedBy", sessionStorage.getItem('user_id')!);
        formData.append("RmiModifiedDate", moment().toString());
        formData.append("SpecCodes", currentValue.SpecCodes ? currentValue.SpecCodes.toString().match(/(?<=\[)[^\][]*(?=])/g).toString() : "");
        formData.append("CompanyIds", currentValue.CompanyIds.toString());
        formData.append("ProviderIds", currentValue.ProviderIds ? currentValue.ProviderIds.toString().match(/(?<=\[)[^\][]*(?=])/g).toString() : "");
        formData.append("File", currentValue.File);

        return props.uploadProviderResources(formData)
          .then((response: any) => {
            closeAddNewDocuments();
            getMemoDocuments();
            for (const file of response) {
              notifier.success(`"${file.RmiFileName}" successfully uploaded for IPA: ${getDisplayCompanyId(file.RmiCompanyId)}.`);
            }
          })
          .catch((err: ErrorResponse) => {
            message.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
            getMemoDocuments();
          });
      }));
    }
  };

  const submitEditDocument = () => {

    const formData = new FormData();
    formData.append("RmiCreatedBy", sessionStorage.user_id);
    formData.append("ProviderIds", editModalData.ProviderIds.length > 0 ? editModalData.ProviderIds.toString().match(/(?<=\[)[^\][]*(?=])/g).toString() : "");
    formData.append("CompanyIds", editModalData.RmiCompanyId);
    formData.append("FileId", editModalData.RmiUid);
    formData.append("SpecCodes", editModalData.SpecCodes.length > 0 ? editModalData.SpecCodes.toString().match(/(?<=\[)[^\][]*(?=])/g).toString() : "");

    return props.updateProviderResources(formData)
      .then(() => {
        notifier.success(`"${editModalData.RmiFileName}" successfully updated.`);
        closeEditDocuments();
        getMemoDocuments();
      })
      .catch((err: ErrorResponse) => {
        message.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
        getMemoDocuments();
      });
  };

  const deleteProviderResources = (record: any) => {
    const formObject = [{
      "RmiUid": record.RmiUid,
      "RmiCompanyId": record.RmiCompanyId,
      "RmiFileDesc": record.RmiFileDesc,
      "RmiFileName": record.RmiFileName,
      "RmiLocationPath": record.RmiFilePath,
      "RmiMaterialType": record.RmiDocumentCategory,
      "RmiLanguage": 'English',
      "RmiCreatedBy": record.RmiUploadBy,
      "RmiCreatedDate": record.RmiUploadDate,
      "RmiModifiedBy": record.RmiUploadBy,
      "RmiModifiedDate": record.RmiUploadDate
    }];

    return props.deleteProviderResources(formObject)
      .then((response: any) => {
        notifier.success(`${response[0].RmiFileName} was successfully deleted.`);
        getMemoDocuments();
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  };

  // customRequest to just return a success to attach file to the form instead being actually uploading yet
  const customRequest = ({ onSuccess }: any) => {
    setTimeout(() => onSuccess('Success'), 0);
  };

  const handleRemove = (index: number) => {
    const fileArray = [...fileAttachments];
    const documentListArray = [...documentList];
    setFileAttachments(fileArray.filter((_, fileIndex) => fileIndex !== index));
    setDocumentList(documentListArray.filter((_, documentIndex) => documentIndex !== index));
  };

  const updateDocumentList = (e: any, index: number, property: string) => {

    const updatedDocumentList = documentList.map((item: DocumentFiles, itemIndex: number) => {
      if (itemIndex === index) {
        if (property === 'companyIds') return { ...item, CompanyIds: e, key: index }
        if (property === 'providerIds') return { ...item, ProviderIds: e, key: index }
        if (property === 'specCodes') return { ...item, SpecCodes: e, key: index }
      }
      return item;
    });

    setDocumentList(updatedDocumentList);
  }

  const updateOneDocument = (e: any, property: string) => {
    if (property === 'providerIds') setEditModalData((previous: any) => ({ ...previous, ProviderIds: e }));
    if (property === 'specCodes') setEditModalData((previous: any) => ({ ...previous, SpecCodes: e }));
  }

  const getAllIpasDesc = () => {
    return props.getAllIpasDesc()
      .then((response: any) => {
        const companyIds = [];
        for (const item of response) {
          companyIds.push({ label: getDisplayCompanyId(item.RmiCompanyId), value: item.RmiCompanyId });
        }
        setCompanyIds(companyIds);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  };

  const searchProviderByName = (providerName: string, companyIds: string[]) => {
    const PAGE_SIZE = '15';
    return props.searchProviderByName(sessionStorage.user_id, PAGE_SIZE, providerName, companyIds)
      .then((response: any) => {
        const providerName: any = [];
        response.forEach((item: any) => {
          if (providerName.filter((e: any) => e.value === `${item.RmiRevFullName} - [${item.RmiProvId}]`).length === 0) {
            providerName.push({
              value: `${item.RmiRevFullName} - [${item.RmiProvId}]`
            });
          }
        });
        setProviderNames(providerName);
        setDefaultProviderNamesOptions(providerName);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  };

  const searchSpecialtyName = (specialtyName: any, companyIds: string[]) => {
    const PAGE_SIZE = '15';
    return props.searchSpecialtyName(sessionStorage.user_id, PAGE_SIZE, specialtyName, companyIds)
      .then((response: any) => {
        const specialty: any = [];
        response.forEach((item: any) => {
          if (specialty.filter((e: any) => e.value === `${item.RmiDescr} - [${item.RmiCode}]`).length === 0) {
            specialty.push({
              value: `${item.RmiDescr} - [${item.RmiCode}]`
            });
          }
        });
        setSpecialties(specialty);
        setDefaultSpecOptions(specialties);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  };

  return (
    <div className="documents-page">
      <div className={`header-title ${styles['memo-title']}`}>
        <Title level={4} className="ml-3">Memos</Title>
      </div>

      {sessionStorage.user_role === UserRole.ADMIN ?
        <Button style={{ 'position': 'absolute', 'right': '0px', 'top': '-35px', 'zIndex': 10 }} onClick={() => setIsAddNewDocumentsVisible(true)}>Add New Document</Button>
      : null}

      <div className="search-section">
        <Table
          className="mt-2"
          columns={memoColumns}
          dataSource={memoDataSource}
          pagination={{ position: ["bottomCenter"], hideOnSinglePage: true }}
          loading={props.isFetchingForMemDoc}
        />
      </div>
      <Modal
        visible={isAddNewDocumentsVisible}
        width={1400}
        maskClosable={false}
        onCancel={closeAddNewDocuments}
        footer={[
          <Button
            key="cancel"
            onClick={closeAddNewDocuments}
          >
            Close
          </Button>,
          <Button
            key="submit"
            type="primary"
            form="attachmentForm"
            onClick={submitNewDocument}
            loading={props.isUploadingProvResources}
          >
            Save
          </Button>,
        ]}
      >
        <Title level={3}>Add Document</Title>

        <Divider/>

        <Form
          form={form}
          name={'documentCategoryForm'}
          onFinish={submitNewDocument}
          initialValues={{
            measureYear: moment().year().toString()
          }}
        >
          <Dragger
            fileList={fileAttachments}
            customRequest={customRequest}
            beforeUpload={beforeUpload}
            multiple={true}
            showUploadList={false}
            accept={'.tif, .tiff, .jpg, .jpeg, .pdf, .doc, .docx, .TIF, .TIFF, .JPG, .JPEG, .PDF, .DOC, .DOCX'}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Click or drag a file at a time to this area to upload</p>
          </Dragger>

          {documentList.length > 0 ?
            <Alert
              className="mt-4"
              message={"Attention: Users can assign documents to either a Provider or a Specialty, but not both. "}
              type={'info'}
            />
            : null
          }

          {documentList.map((option: DocumentFiles, index: number) => {
            return <>
              <div className="row">
                <div className="col-lg-24 col-md-24">
                  <Form.Item>
                    <table className="mt-4" style={{ width: '100%' }}>
                      <tbody>
                        <tr>
                          <td key={index} style={{padding: '0 10px', border: '1px solid #d9d9d9', borderRadius: '2px', width: '250px'}}>
                            {option.File.name}
                            <span style={{float: 'right', marginRight: '5px'}}>
                              <DeleteFilled className="ml-1" onClick={() => handleRemove(index)} />
                            </span>
                          </td>
                          <td>
                            <Select
                              key={index}
                              mode="multiple"
                              value={documentList[index].CompanyIds || []}
                              showArrow
                              style={{ width: '250px' }}
                              options={companyIds}
                              placeholder="Select Company ID"
                              dropdownMatchSelectWidth={false}
                              onChange={(value) => updateDocumentList(value, index, 'companyIds')}
                            />
                          </td>
                          <td>
                            <Select
                              key={index}
                              value={documentList[index].ProviderIds || []}
                              mode="multiple"
                              showArrow
                              style={{ width: '425px' }}
                              options={providerNames}
                              onSearch={(value) => searchProviderByName(value, documentList[index].CompanyIds)}
                              onChange={(value) => updateDocumentList(value, index, 'providerIds')}
                              placeholder="Provider"
                              dropdownMatchSelectWidth={false}
                              disabled={documentList[index].CompanyIds?.length === 0 || (documentList[index].CompanyIds?.length > 0 && documentList[index].SpecCodes?.length > 0)}
                            />
                          </td>
                          <td>
                            <Select
                              key={index}
                              value={documentList[index].SpecCodes || []}
                              mode="multiple"
                              showArrow
                              style={{ width: '425px' }}
                              options={specialties}
                              onSearch={(value) => searchSpecialtyName(value, documentList[index].CompanyIds) }
                              onChange={(value) => updateDocumentList(value, index, 'specCodes')}
                              placeholder="Specialty"
                              dropdownMatchSelectWidth={false}
                              disabled={documentList[index].CompanyIds?.length === 0 || (documentList[index].CompanyIds?.length > 0 && documentList[index].ProviderIds?.length > 0)}
                            />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </Form.Item>
                </div>
              </div>
            </>
          })}
        </Form>
      </Modal>

      <Modal
        visible={showEditDocumentModal}
        width={1400}
        maskClosable={false}
        onCancel={closeEditDocuments}
        footer={[
          <Button
            key="cancel"
            onClick={closeEditDocuments}
          >
            Close
          </Button>,
          <Button
            key="submit"
            type="primary"
            form="attachmentForm"
            onClick={submitEditDocument}
            loading={props.isUpdatingDocument}
          >
            Save
          </Button>,
        ]}
      >
        <Title level={3}>Edit Document</Title>

        <Divider />

        <Alert
          className="mt-4"
          message={"Attention: Users can assign documents to either a Provider or a Specialty, but not both. "}
          type={'info'}
        />

        <Form
          form={form}
          name={'editDocumentForm'}
          onFinish={submitEditDocument}
        >
          <div className="row">
            <div className="col-lg-24 col-md-24">
              <Form.Item>
                <table className="mt-4" style={{ width: '100%' }}>
                  <tbody>
                    <tr>
                      <td style={{padding: '0 10px', border: '1px solid #d9d9d9', borderRadius: '2px', width: '250px'}}>
                        {getDisplayCompanyId(editModalData.RmiCompanyId)}
                      </td>
                      <td style={{padding: '0 10px', border: '1px solid #d9d9d9', borderRadius: '2px', width: '250px'}}>
                        {editModalData.RmiFileName}
                      </td>
                      <td>
                        <Select
                          mode="multiple"
                          showArrow
                          style={{ width: '425px' }}
                          options={defaultProviderNamesOptions}
                          onSearch={(value) => searchProviderByName(value, [editModalData.RmiCompanyId])}
                          onChange={(value) => updateOneDocument(value, 'providerIds')}
                          value={editModalData.ProviderIds}
                          placeholder="Provider"
                          dropdownMatchSelectWidth={false}
                          filterOption={false}
                          disabled={editModalData.SpecCodes?.length > 0}
                        />
                      </td>
                      <td>
                        <Select
                          mode="multiple"
                          showArrow
                          style={{ width: '425px' }}
                          onSearch={(value) => searchSpecialtyName(value, [editModalData.RmiCompanyId]) }
                          onChange={(value) => updateOneDocument(value, 'specCodes')}
                          value={editModalData.SpecCodes}
                          options={defaultSpecOptions}
                          placeholder="Specialty"
                          dropdownMatchSelectWidth={false}
                          filterOption={false}
                          disabled={editModalData.ProviderIds?.length > 0}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </Form.Item>
            </div>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

const mapStateToProps = (store: RootStateOrAny) => ({
  isFetchingForMemDoc: store.rootReducer.eligibilityDocuments.isFetchingForMemDoc, //memos table
  isUpdatingDocument: store.rootReducer.appIPA.isUpdatingDocument,
  isUploadingProvResources: store.rootReducer.appIPA.isUploadingProvResources,
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators(
  {
    uploadProviderResources,
    getAllIpasDesc,
    searchProviderByName,
    searchSpecialtyName,
    deleteProviderResources,
    getCategoryDocsForMemDocPage,
    getProviderByFileId,
    getSpecByFileId,
    updateProviderResources,
    downloadFile,
    updateReadInfo,

    //eligibility info page related
    search,
  },
  dispatch
);

export default connect(mapStateToProps, mapDispatchToProps)(MemoPage);
